'use client'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'

import { ModalDescriptionReferFriend } from '@/components/common/refer-friend/modal-description-refer'
import { Button } from '@/components/ui/button'
import { useIsDesktopClient } from '@/hooks/common/use-device-type'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { formatPrice } from '@/lib/utils'
import { RANKING_LABEL, TUserProfile, UserRankingFormatted } from '@/types/auth'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

export const UserSheet = ({
  user,
  coolClubMember,
}: {
  user: TUserProfile
  coolClubMember: UserRankingFormatted
}) => {
  const isDesktop = useIsDesktopClient()

  const [isOpen, setIsOpen] = useState(false)
  const [isModalDescriptionVoucher, setIsModalDescriptionVoucher] =
    useState(false)
  const [isModalDescriptionReferFriend, setIsModalDescriptionReferFriend] =
    useState(false)

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <button
            className='ml-3 flex h-13 w-10.5 cursor-pointer items-center justify-center outline-none'
            aria-label='Thông tin tài khoản'
          >
            <Image
              src={coolClubMember?.user_image}
              alt='account'
              width={30}
              height={30}
            />
          </button>
        </SheetTrigger>
        <SheetContent
          side='right'
          className='w-full border-0 p-0 focus:border-0 focus:ring-0 md:w-[450px]'
          showCloseButton={false}
        >
          <div className='flex h-full flex-col justify-between'>
            <SheetTitle className='border-b px-2 py-6 md:px-7'>
              <p className='text-3xl font-normal'>Hi, {user.fullname}</p>
              <Image
                src={`/images/account/member-${RANKING_LABEL[coolClubMember.ranking_id]}.png`}
                alt='Medal'
                width={88}
                height={33}
                className='w-auto object-contain'
                unoptimized
                loading='lazy'
              />
            </SheetTitle>
            <SheetDescription />

            <div className='relative flex-1 overflow-auto px-2 py-5 md:px-7 md:pr-5'>
              <Link href='/tam-nhin-2030'>
                <Image
                  src={ImageHelper.getImageUrl(
                    '/uploads/March2025/mceclip5_91.png'
                  )}
                  alt='Medal'
                  width={352}
                  height={48}
                  className='w-full object-cover'
                  unoptimized
                  loading='lazy'
                />
              </Link>

              <div className='mt-4 rounded-md bg-neutral-100 px-4 py-3'>
                <div>
                  {(coolClubMember?.next_ranking_value ?? 0) > 0 && (
                    <>
                      <p className='mb-1 text-sm leading-normal text-neutral-900'>
                        Chi tiêu thêm
                      </p>
                      <p className='text-2xl font-medium text-primary/90'>
                        {formatPrice(coolClubMember?.next_ranking_value ?? 0)}
                      </p>
                      <div className='flex items-center gap-1'>
                        <p className='text-sm text-neutral-900'>
                          để lên hạng lên hạng
                        </p>
                        <Image
                          src={coolClubMember.next_ranking_image || ''}
                          alt='Medal'
                          width={72}
                          height={26}
                        />
                      </div>
                    </>
                  )}
                  {coolClubMember.ranking_slug === 'hang-bach-kim' && (
                    <p className='mb-0.5 font-sans text-sm leading-4.5 text-neutral-900'>
                      Chiến thần mua sắm!
                    </p>
                  )}
                  <div className='relative h-1 w-full bg-neutral-200'>
                    <div
                      className='absolute top-0 h-1 bg-primary'
                      style={{
                        width: `${coolClubMember.percent}%`,
                      }}
                    ></div>
                    <span className='absolute left-0 top-2 before:absolute before:-left-1 before:-top-3 before:h-3 before:w-3 before:rounded-full before:border-[0.125rem] before:border-light before:bg-primary'>
                      <Image
                        src={ImageHelper.getImageUrl(
                          '/image/September2023/mceclip0_80.png',
                          '100_113'
                        )}
                        alt='Medal'
                        width={25}
                        height={26}
                        unoptimized
                      />
                    </span>
                    <span className='absolute left-1/3 top-2 before:absolute before:-left-1 before:-top-3 before:h-3 before:w-3 before:rounded-full before:border-[0.125rem] before:border-light before:bg-primary'>
                      <Image
                        src={ImageHelper.getImageUrl(
                          '/image/September2023/mceclip2_55.png',
                          '100_113'
                        )}
                        alt='Medal'
                        width={25}
                        height={26}
                        unoptimized
                      />
                    </span>
                    <span className='absolute left-3/4 top-2 before:absolute before:-left-1 before:-top-3 before:h-3 before:w-3 before:rounded-full before:border-[0.125rem] before:border-light before:bg-primary'>
                      <Image
                        src={ImageHelper.getImageUrl(
                          '/image/September2023/mceclip3_94.png',
                          '100_113'
                        )}
                        alt='Medal'
                        width={25}
                        height={26}
                        unoptimized
                      />
                    </span>
                    <span className='absolute right-0 top-2 before:absolute before:-right-1 before:-top-3 before:h-3 before:w-3 before:rounded-full before:border-[0.125rem] before:border-light before:bg-primary'>
                      <Image
                        src={ImageHelper.getImageUrl(
                          '/image/September2023/mceclip1_100.png',
                          '100_113'
                        )}
                        alt='Medal'
                        width={25}
                        height={26}
                        unoptimized
                      />
                    </span>
                  </div>
                  <p className='mt-10 font-sans text-xs italic leading-3.5 text-neutral-700/80'>
                    {` Hạng thành viên được vừa được xét lại vào ngày
                    ${coolClubMember.date_reset_ranking.now}, ngày xét hạng tiếp
                    theo: ${coolClubMember.date_reset_ranking.next}`}
                  </p>
                </div>
              </div>

              <div className='mt-2 flex items-stretch gap-4'>
                <div className='basis-3/5 rounded-md bg-neutral-100 p-3'>
                  <p className='text-sm text-neutral-600'>Bạn đang có</p>
                  <div className='flex items-center gap-1'>
                    <Image
                      src={'/icons/icon-link-box.svg'}
                      alt='Medal'
                      width={16}
                      height={16}
                    />
                    <span className='whitespace-nowrap text-lg font-medium text-neutral-900'>
                      {formatPrice(coolClubMember.total_point).replace(
                        /đ/g,
                        ' '
                      )}
                      Coolcash
                    </span>
                  </div>
                  <p className='text-sm text-neutral-600'>
                    Chờ:{' '}
                    {formatPrice(coolClubMember.total_point_pending).replace(
                      /đ/g,
                      ' '
                    )}{' '}
                    Coolcash
                  </p>
                </div>
                <Link
                  href={
                    '/page/coolclub-chuong-trinh-khach-hang-than-thiet-cua-coolmate'
                  }
                  className='flex-1 rounded-md bg-neutral-900 p-3 text-light'
                >
                  Về <br />
                  CoolClub
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='h-6 w-6'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='2'
                      d='M14 5l7 7m0 0l-7 7m7-7H3'
                    />
                  </svg>
                </Link>
              </div>
              <div
                className='my-4 rounded-lg bg-cover px-6 py-4 text-light'
                style={{
                  backgroundImage: `url(${ImageHelper.getImageUrl(
                    '/image/September2024/mceclip1.png'
                  )})`,
                }}
              >
                <h3 className='text-xl font-medium'>Giới thiệu bạn bè</h3>
                <p className='mt-1 text-sm'>
                  <strong>Nhận 10%</strong> giá trị đơn hàng đầu tiên của <br />{' '}
                  bạn bè được quy đổi thành CoolCash
                </p>
                <div className='mt-4 flex gap-2'>
                  <Button
                    variant='white'
                    onClick={() => {
                      setIsModalDescriptionReferFriend(true)
                      setIsOpen(false)
                    }}
                  >
                    CHIA SẺ
                  </Button>
                  <Button
                    variant='outline-neutral'
                    asChild
                    className='rounded-full border border-light px-6 py-2 text-light hover:border-primary-500 hover:bg-transparent active:border-primary-500 active:bg-transparent'
                  >
                    <Link href='/account/refer-friend'>TÌM HIỂU THÊM</Link>
                  </Button>
                </div>
              </div>
              {/* <div className='rounded-lg bg-neutral-100 p-4 py-5'>
                <p className='mb-4 font-medium text-neutral-900'>
                  Ưu đãi thành viên
                </p>

                <div className='relative h-[128px] rounded-lg bg-light pl-6 pr-4.5 before:absolute before:left-0 before:top-1/2 before:h-6.5 before:w-6 before:-translate-x-[53%] before:-translate-y-1/2 before:rounded-full before:bg-neutral-100'>
                  <div className='flex h-full flex-col justify-between border-l border-dashed border-l-neutral-300 px-4 py-5'>
                    {coolClubMember.voucher.code && (
                      <p className='text-base font-medium uppercase leading-4.5 text-neutral-700'>
                        {coolClubMember.voucher.code}
                      </p>
                    )}
                    <p className='text-sm leading-4.5 text-neutral-400'>
                      {coolClubMember.voucher.description}
                    </p>
                    <button
                      className='flex items-center gap-1 text-xs text-primary'
                      onClick={() => setIsModalDescriptionVoucher(true)}
                    >
                      {coolClubMember.voucher.buttonText}
                      <Image
                        src={ImageHelper.getImageUrl(
                          '/image/April2024/mceclip2_20.png',
                          '100_113'
                        )}
                        alt='arrow'
                        width={12}
                        height={12}
                        loading='lazy'
                        unoptimized
                      />
                    </button>
                    <Image
                      src={ImageHelper.getImageUrl(
                        '/image/April2024/mceclip2_45.png'
                      )}
                      alt='gift icon'
                      unoptimized
                      width={165}
                      height={96}
                      loading='lazy'
                      className='absolute bottom-0 right-0'
                    />
                  </div>
                  <span className='absolute -top-2.5 left-9 rounded bg-gradient-to-b from-[#203e8f] to-[#2a50b9] px-2.5 py-0.5 text-center text-xs text-light'>
                    {coolClubMember.voucher.label}
                  </span>
                </div>
              </div> */}

              <ul className='mt-2 grid grid-cols-3 items-stretch gap-2'>
                {menus.map((menu) => (
                  <li key={menu.href}>
                    <Link
                      href={menu.href}
                      className='flex h-full flex-col items-center justify-center rounded-lg bg-neutral-100 py-5 hover:opacity-80'
                    >
                      <Image
                        src={ImageHelper.getImageUrl(menu.iconUrl)}
                        alt='Voucher'
                        width={42}
                        height={42}
                        className='object-cover'
                        loading='lazy'
                        unoptimized
                      />
                      <p className='w-2/3 text-center text-sm text-neutral-900'>
                        {menu.label}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <Link
              href={isDesktop ? '/account/info' : '/account'}
              className='bottom-0 left-0 mt-4 flex h-16 w-full items-center justify-center bg-primary text-light'
              onClick={() => setIsOpen(false)}
            >
              ĐI ĐẾN TÀI KHOẢN
            </Link>
          </div>
        </SheetContent>
      </Sheet>
      {isModalDescriptionVoucher && (
        <VoucherDescriptionModal
          onClose={() => setIsModalDescriptionVoucher(false)}
        />
      )}

      {isModalDescriptionReferFriend && (
        <ModalDescriptionReferFriend
          isOpen={isModalDescriptionReferFriend}
          onClose={() => {
            setIsModalDescriptionReferFriend(false)
            setIsOpen(true)
          }}
          cb={() => {
            setIsOpen(false)
            setIsModalDescriptionReferFriend(false)
          }}
        />
      )}
    </>
  )
}

export const VoucherDescriptionModal = ({
  onClose,
}: {
  onClose: () => void
}) => {
  return (
    <ResponsiveModal open onOpenChange={onClose}>
      <ResponsiveModalTrigger />

      <ResponsiveModalContent
        className='z-[103] gap-0 border-none bg-light px-6 py-9 lg:w-[550px] lg:max-w-full lg:rounded-md'
        aria-describedby={'modal-login'}
        overlayClassName='z-[103]'
      >
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />
        <div className='text-sm leading-5 text-neutral-900/80'>
          <p>Happy Friday X2 CoolCash</p>
          <p>
            Thứ 6 hàng tuần, Hội viên CoolClub được nhân đôi hoàn tiền CoolCash
            khi đăng nhập và mua hàng trên website Coolmate.*
          </p>
          <p>
            <strong className='text-neutral-900'>Ví dụ:</strong> Hội viên
            CoolClub đăng nhập và mua 1 đơn hàng trị giá 500.000đ trên website
            Coolmate được hoàn tiền như sau:
          </p>
          <picture>
            <source
              srcSet={
                'https://mcdn.coolmate.me/image/April2024/mceclip2_42.png'
              }
              media='(max-width: 991px)'
            />
            <Image
              src={'https://mcdn.coolmate.me/image/April2024/mceclip0_46.png'}
              width={522}
              height={118}
              loading='lazy'
              alt='Happy Friday X2 CoolCash'
              className='my-2'
              unoptimized
            />
          </picture>
          <p className='text-neutral-900'>
            *Chương trình áp dụng thứ Sáu hằng tuần, bắt đầu từ 19/04/2024.
          </p>
          {/* <div className='x2-coolcash-action'>
        <div className='x2-coolcash-action__wrapper'>
          <p>
            Để không bỏ lỡ hoàn tiền CoolCash cho bất kỳ đơn hàng nào:
          </p>
          <div className='x2-coolcash-action__cta'>
            <Link href='' className='btn btn--black'>
              tham gia coolclub
            </Link>
          </div>
        </div>
      </div> */}
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}

const menus = [
  {
    label: 'Ví Voucher',
    href: '/account/voucher-wallet',
    iconUrl: '/image/September2023/mceclip1_59.png',
  },
  {
    label: 'Lịch sử đơn hàng',
    href: '/account/orders',
    iconUrl: '/image/September2023/mceclip0_18.png',
  },
  {
    label: 'Sổ địa chỉ',
    href: '/account/user-address',
    iconUrl: '/image/September2023/mceclip2_76.png',
  },
  {
    label: 'Cài đặt tài khoản',
    href: '/account/info',
    iconUrl: '/image/September2023/mceclip6_34.png',
  },
  {
    label: 'Đánh giá và phản hồi',
    href: '/account/feedback',
    iconUrl: '/image/September2023/mceclip3_71.png',
  },
  {
    label: 'FAQ & Chính sách',
    href: '/account/faq',
    iconUrl: '/image/September2023/mceclip5_95.png',
  },
]
